import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userAPI, franchiseAPI, metaAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Shield, UserCog, Edit } from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

export default function UsersAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: '', franchise_id: '' });
  const [userroles, setUserRoles] = useState([]);
  const [editingUser, setEditingUser] = useState(null);

  const fetchMeta = async () => {
    try {
      const [res] = await Promise.all([
        metaAPI.getUserRoles()
      ]);

      setUserRoles(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);

    setFormData({
      name: u.name || '',
      email: u.email || '',
      password: '',
      role: u.role,
      franchise_id: u.franchise_id || ''
    });

    setShowForm(true);
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchMeta(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, franchisesRes] = await Promise.all([
        userAPI.getAll(),
        franchiseAPI.getAll(),
      ]);
      setUsers(usersRes.data);

      const mappedFranchises = [
        ...franchisesRes.data.map(f => ({
          label: f.name,
          value: f.id
        }))
      ];

      setFranchises(mappedFranchises);
      
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser){

        const payload = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          franchise_id: formData.franchise_id
        };

        // only send password if user entered it
        if (formData.password) {
          payload.password = formData.password;
        }

        await userAPI.update(editingUser.id, payload);

        toast.success('User updated successfully');
      } else {
        await userAPI.create(formData);
        toast.success('User created successfully');
      }

      setShowForm(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: '', franchise_id: '' });
      fetchData();

    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create user');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-purple-100 text-purple-800',
      franchise_admin: 'bg-blue-100 text-blue-800',
      staff: 'bg-green-100 text-green-800',
    };
    return styles[role] || 'bg-slate-100 text-slate-800';
  };

  return (
    <AdminLayout activeItem="Users">
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-medium text-brand-primary font-outfit">Users</h1>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingUser(null);
              setFormData({ name: '', email: '', password: '', role: '', franchise_id: '' });
            }}
            className="flex items-center bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200"
            data-testid="add-user-button"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add User
          </button>
        </div>
      </header>

      <main className="p-8">
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="user-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
            <div className="bg-white p-8 max-w-md w-full mx-4 border border-slate-200">
              <h2 className="text-xl font-medium text-brand-primary mb-6">
                {editingUser ? 'Edit User' : 'Add User'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent" data-testid="user-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent" data-testid="user-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input 
                    type="password"
                    required={!editingUser} 
                    minLength={6} 
                    value={formData.password} 
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent" data-testid="user-password-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <SearchableSelect
                    value={formData.role}
                    onChange={(val) => setFormData({ ...formData, role: val })}
                    options={userroles}
                    placeholder="Select Role"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Franchise</label>
                  <SearchableSelect
                    value={formData.franchise_id}
                    onChange={(val) => setFormData({ ...formData, franchise_id: val })}
                    options={franchises}
                    placeholder="Select Franchise"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200" data-testid="user-submit-button">
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                  <button type="button" onClick={() => {setShowForm(false);setEditingUser(null);}} className="flex-1 border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-2 transition-all duration-200">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Role</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Franchise</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-medium text-slate-800 flex items-center">
                        {u.role === 'super_admin' ? <Shield className="h-4 w-4 text-purple-600 mr-2" /> : <UserCog className="h-4 w-4 text-slate-400 mr-2" />}
                        {u.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 text-xs font-medium ${getRoleBadge(u.role)}`}>
                          {u.role.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{u.franchise_name || '-'}</td>
                      <td className="px-6 py-4 text-left text-sm text-slate-600">
                        <button onClick={() => handleEdit(u)}
                          className="inline-flex items-center py-1 text-sm text-brand-accent hover:text-brand-accent-hover mr-2"
                          data-testid={`edit-product-${u.id}`}>
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </AdminLayout>
  );
}
